// RESONATE_AD_VARIANTS_V1
// Message-match config for paid ad landing pages. Each Reddit ad's utm_content
// value maps to a hero variant that echoes the ad headline. This lifts
// conversion 20-40% by eliminating the "did I land on the right page?" bounce.
//
// To add a new ad variant:
//   1. Add an entry keyed by the ad's `utm_content` slug.
//   2. Ship - the hero picks it up automatically from ?utm_content=<slug>.
//
// Variants can be split by persona so we can render on / (founder) or
// /agencies (agency) with the same slug space and no route changes.

import { useEffect, useState } from "react";

export const AD_VARIANTS = {
  //
  // ─── FOUNDERS ──────────────────────────────────────────────────
  //
  chat_to_your_linkedin_mcp: {
    persona: "founder",
    eyebrow: "Built for AI-native founders",
    headline: ["Can't chat to your", "LinkedIn? You can."],
    subhead:
      "Resonate MCP plugs your LinkedIn into Claude, ChatGPT, or Cursor. Ask it what to post, why the last one flopped, or to draft five variations in your voice. Live in your workflow.",
    ctaPrimary: "Try Resonate MCP",
    ctaSecondary: "See how MCP works",
  },
  best_posts_already_written_integrations: {
    persona: "founder",
    eyebrow: "Founder-led marketing, without the ghostwriter",
    headline: ["Your best posts are", "already written."],
    subhead:
      "They're sitting in your Notion, Slack, GitHub and HubSpot right now. Resonate connects to your stack, surfaces the ones worth telling, and drafts them in your voice.",
    ctaPrimary: "Connect your tools",
    ctaSecondary: "See how it works",
  },
  no_time_no_ideas_ai_slop: {
    persona: "founder",
    eyebrow: "Founders like you built this",
    headline: ["No time. No clue what to post.", "Sick of AI slop. Same."],
    subhead:
      "Resonate trains on your real writing and pulls ideas straight from what you're already doing. Drafts land in 60 seconds. Nothing generic ever ships.",
    ctaPrimary: "Kill the slop",
    ctaSecondary: "See a draft in your voice",
  },
  robot_wearing_your_name: {
    persona: "founder",
    eyebrow: "Trained on your real writing",
    headline: ["You'd automate LinkedIn", "if it didn't sound like a robot", "wearing your name."],
    subhead:
      "Every other tool prints the same voice for every user. Resonate trains a private voice model on your actual writing - sentence rhythm, vocabulary, how you open and close. Automation without the AI tell.",
    ctaPrimary: "Train my voice model",
    ctaSecondary: "Hear the difference",
  },
  predict_engagement_before_you_post: {
    persona: "founder",
    eyebrow: "The engagement prediction engine",
    headline: ["Predict engagement", "before you post."],
    subhead:
      "Resonate scores every draft against real LinkedIn performance data - hook strength, format, topic fit - and learns from every publish. The longer you use it, the better it gets.",
    ctaPrimary: "Score my next post",
    ctaSecondary: "See the prediction engine",
  },
  ai_chats_to_linkedin_posts_mcp: {
    persona: "founder",
    eyebrow: "Plugs into Claude, ChatGPT, Cursor",
    headline: ["Turn your AI chats", "into viral LinkedIn posts."],
    subhead:
      "Talking to Claude, ChatGPT or Cursor all day? Every idea you push through them can become a post - in your voice - the moment you finish. Resonate MCP plugs directly into your AI.",
    ctaPrimary: "Install Resonate MCP",
    ctaSecondary: "How it works",
  },
  too_busy_building_to_post: {
    persona: "founder",
    eyebrow: "Built for founders who ship",
    headline: ["Too busy building to post?", "We post about what you just built."],
    subhead:
      "Resonate reads your GitHub commits, Notion docs, and Slack wins, then drafts posts in your voice about the work you did today. Ship the code, ship the post.",
    ctaPrimary: "Post about my last commit",
    ctaSecondary: "See it in action",
  },

  //
  // ─── AGENCIES / GHOSTWRITERS ───────────────────────────────────
  //
  ceo_ideas_buried_in_slack: {
    persona: "agency",
    eyebrow: "For ghostwriters running B2B founders",
    headline: ["Your CEO's best ideas", "are buried in Slack."],
    subhead:
      "Resonate connects to your client's Slack, Notion and GitHub, surfaces the posts worth writing, and drafts them in their voice - not yours. One console per client, magic-link approvals, ban-safe by design.",
    ctaPrimary: "Uncover client ideas",
    ctaSecondary: "How the console works",
  },
  clients_too_busy_too_smart: {
    persona: "agency",
    eyebrow: "For agencies scaling past 5 clients",
    headline: ["Your clients are too busy to post.", "Too smart not to. Write for them."],
    subhead:
      "Trained voice model per client. Magic-link approvals - no extra seats. White-label ROI reports. $299/mo for the console plus $99 per active client - you pay Resonate, you bill your clients.",
    ctaPrimary: "Start the agency console",
    ctaSecondary: "See the client workflow",
  },
  founder_wont_post_7_minutes: {
    persona: "agency",
    eyebrow: "For ghostwriters, in 7 minutes a client",
    headline: ["Your founder won't post.", "Do it for them in 7 minutes a week."],
    subhead:
      "Voice-matched drafts from their real work, magic-link approval on their phone, scheduled and safe. Run 3, 10, or 40 clients from one console - $99 a month each, and your clients never see our pricing.",
    ctaPrimary: "Run 3 clients in 7 min/wk",
    ctaSecondary: "See the console",
  },
};

/**
 * Read UTM params from ANY location in the current URL - query string
 * (`?utm_source=x`) OR after the hash (`#agency-hero/?utm_source=x` - a
 * malformed pattern currently on some live Reddit ads).
 *
 * Regex is intentionally permissive; we only extract keys we care about.
 */
export const readUtmFromCurrentUrl = () => {
  if (typeof window === "undefined") return {};
  const url = window.location.href;
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_persona", "utm_term"];
  const out = {};
  keys.forEach((k) => {
    const m = url.match(new RegExp(`[?&]${k}=([^&#]+)`));
    if (m) {
      try { out[k] = decodeURIComponent(m[1]); } catch { out[k] = m[1]; }
    }
  });
  return out;
};

// Short hash aliases for organic / manual deep links (e.g. /#slack).
// Reddit ads use ?utm_content=<full slug>, but marketing/blog links can use these.
const HASH_ALIAS = {
  slack: "ceo_ideas_buried_in_slack",
  "no-time": "no_time_no_ideas_ai_slop",
  ghostwriter: "clients_too_busy_too_smart",
  mcp: "chat_to_your_linkedin_mcp",
  ideas: "best_posts_already_written_integrations",
  robot: "robot_wearing_your_name",
  predict: "predict_engagement_before_you_post",
  building: "too_busy_building_to_post",
  buried: "ceo_ideas_buried_in_slack",
  "7-minutes": "founder_wont_post_7_minutes",
};

/**
 * Return the ad variant that matches the current URL (or null if none).
 * Match order: (1) `?utm_content=<slug>`, (2) `#<slug>` exact,
 * (3) `#<alias>` via HASH_ALIAS. Optionally filter by persona.
 */
export const getAdVariantFromUrl = (personaFilter = null) => {
  if (typeof window === "undefined") return null;
  const utm = readUtmFromCurrentUrl();
  let slug = utm.utm_content;

  if (!slug && window.location.hash) {
    // Strip leading '#' and anything after a '/' or '?' (handles the malformed
    // '#agency-hero/?utm...' pattern by isolating the first segment).
    const raw = window.location.hash.replace(/^#/, "").split(/[/?&]/)[0];
    if (raw) {
      if (AD_VARIANTS[raw]) slug = raw;
      else if (HASH_ALIAS[raw]) slug = HASH_ALIAS[raw];
    }
  }

  if (!slug) return null;
  const variant = AD_VARIANTS[slug];
  if (!variant) return null;
  if (personaFilter && variant.persona !== personaFilter) return null;
  return { ...variant, slug, utm };
};

/**
 * Hydration-safe wrapper around getAdVariantFromUrl, used by Hero/AgencyHero/
 * MobileStickyCta. scripts/ssg.cjs always prerenders with no query
 * string/hash, so the server-rendered HTML never has a variant - reading the
 * real URL synchronously during the first client render (as a plain
 * useMemo(() => getAdVariantFromUrl(...)) did) mismatches that server output
 * for any visitor who *does* have ?utm_content=.../#... and makes React
 * discard + client-rerender the whole subtree. When hydrating a prerendered
 * page (window.__SSG_HYDRATING__, set in src/index.js) this starts at null
 * to match the server, then corrects via effect right after mount - same
 * near-instant swap, no mismatch. A genuine client-only render (no SSG match)
 * still reads the variant immediately, same as before.
 * `locationKey` should be `${pathname}${search}${hash}` so this reacts to
 * client-side navigation exactly like the old useMemo deps did.
 */
export const useAdVariant = (personaFilter, locationKey) => {
  const [variant, setVariant] = useState(() =>
    typeof window !== "undefined" && window.__SSG_HYDRATING__
      ? null
      : getAdVariantFromUrl(personaFilter)
  );
  useEffect(() => {
    setVariant(getAdVariantFromUrl(personaFilter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaFilter, locationKey]);
  return variant;
};
